import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import {
  PAYMENT_PROCESSOR_STATUSES,
  type PaymentProcessorStatusSchema,
} from '@erp/rpc';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { useSupplierSearch } from '@/api/rpc/hooks/accounts-payable/useSupplierSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { useDebouncedSearchInput } from '@/hooks/useDebouncedSearchInput';

type ProcessorStatus = z.infer<typeof PaymentProcessorStatusSchema>;

const SUPPLIER_SEARCH_LIMIT = 50;

interface VendorFilterProps {
  value: string[];
  onChange: (supplierIds: string[]) => void;
  className?: string;
}

/** Autocomplete multiselect over suppliers; selected ids resolve to names. */
export function VendorFilter({
  value,
  onChange,
  className,
}: VendorFilterProps) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: found } = useSupplierSearch({
    search: debouncedSearch || undefined,
    limit: SUPPLIER_SEARCH_LIMIT,
  });
  const { data: selectedData } = useSupplierSearch(
    { ids: value },
    { enabled: value.length > 0 },
  );

  const names = new Map(
    (selectedData?.suppliers ?? []).map((s) => [s.id, s.name]),
  );
  const selected: MultiSelectOption[] = value.map((id) => ({
    id,
    label: names.get(id) ?? id,
  }));

  const merged = new Map<string, MultiSelectOption>();
  for (const s of found?.suppliers ?? []) {
    merged.set(s.id, { id: s.id, label: s.name });
  }
  for (const s of selected) merged.set(s.id, s);
  const options = [...merged.values()];

  return (
    <MultiSelect
      options={options}
      value={selected}
      onChange={(opts) => onChange(opts.map((o) => o.id))}
      onSearchChange={setSearch}
      placeholder={gt('All vendors', {
        context: 'payments filter placeholder, no vendors selected',
      })}
      searchPlaceholder={gt('Search vendors...', {
        context: 'payments vendor filter search input placeholder',
      })}
      aria-label={gt('Vendor', { context: 'payments filter' })}
      size="sm"
      shouldAutoSort={false}
      className={className ?? 'h-8 min-w-[180px]'}
    />
  );
}

interface ProcessorStatusFilterProps {
  value: ProcessorStatus[];
  onChange: (statuses: ProcessorStatus[]) => void;
}

function isProcessorStatus(id: string): id is ProcessorStatus {
  return (PAYMENT_PROCESSOR_STATUSES as readonly string[]).includes(id);
}

export function ProcessorStatusFilter({
  value,
  onChange,
}: ProcessorStatusFilterProps) {
  const gt = useGT();
  const labels: Record<ProcessorStatus, string> = {
    PAID: gt('Paid', { context: 'payment processor status' }),
    IN_PROCESS: gt('In process', { context: 'payment processor status' }),
    UNPAID: gt('Unpaid', { context: 'payment processor status' }),
    VOID: gt('Void', { context: 'payment processor status' }),
    EXPIRED: gt('Expired', { context: 'payment processor status' }),
    PRINTED: gt('Printed', { context: 'payment processor status' }),
    MAILED: gt('Mailed', { context: 'payment processor status' }),
    FAILED: gt('Failed', { context: 'payment processor status' }),
    REFUNDED: gt('Refunded', { context: 'payment processor status' }),
  };
  const options: MultiSelectOption[] = PAYMENT_PROCESSOR_STATUSES.map((s) => ({
    id: s,
    label: labels[s],
  }));

  const wanted = new Set<string>(value);
  const selected = options.filter((o) => wanted.has(o.id));

  return (
    <MultiSelect
      options={options}
      value={selected}
      onChange={(opts) => {
        const statuses: ProcessorStatus[] = [];
        for (const o of opts) if (isProcessorStatus(o.id)) statuses.push(o.id);
        onChange(statuses);
      }}
      placeholder={gt('All processor statuses', {
        context: 'payments filter placeholder, no processor status selected',
      })}
      searchPlaceholder={gt('Search statuses...', {
        context: 'payments processor status filter search placeholder',
      })}
      aria-label={gt('Processor status', { context: 'payments filter' })}
      size="sm"
      shouldAutoSort={false}
      className="h-8 min-w-[200px]"
    />
  );
}

interface PaymentsSearchInputProps {
  committed: string | undefined;
  onCommit: (value: string | undefined) => void;
}

export function PaymentsSearchInput({
  committed,
  onCommit,
}: PaymentsSearchInputProps) {
  const gt = useGT();
  const search = useDebouncedSearchInput({ committed, onCommit });
  return (
    <div className="relative w-72">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
        placeholder={gt('Search invoice, order, reference, vendor, amount…', {
          context: 'payments search input placeholder',
        })}
        aria-label={gt('Search payments', { context: 'payments filter' })}
        className="h-8 pl-8"
      />
    </div>
  );
}

interface ListFilterSearch {
  vendor?: string[];
  q?: string;
  processorStatus?: ProcessorStatus[];
}

interface PaymentsListFilterBarProps {
  search: ListFilterSearch;
  onChange: (updates: ListFilterSearch & { page?: undefined }) => void;
}

/** Search, vendor and processor-status filters plus a Clear filters button. */
export function PaymentsListFilterBar({
  search,
  onChange,
}: PaymentsListFilterBarProps) {
  // Remounts the search box so a cleared filter also drops pending keystrokes.
  const [clearNonce, setClearNonce] = useState(0);
  const hasFilters = Boolean(
    search.vendor?.length || search.q || search.processorStatus?.length,
  );

  const clear = () => {
    setClearNonce((n) => n + 1);
    onChange({
      vendor: undefined,
      q: undefined,
      processorStatus: undefined,
      page: undefined,
    });
  };

  return (
    <>
      <PaymentsSearchInput
        key={clearNonce}
        committed={search.q}
        onCommit={(q) => onChange({ q, page: undefined })}
      />
      <VendorFilter
        value={search.vendor ?? []}
        onChange={(vendor) =>
          onChange({
            vendor: vendor.length ? vendor : undefined,
            page: undefined,
          })
        }
      />
      <ProcessorStatusFilter
        value={search.processorStatus ?? []}
        onChange={(processorStatus) =>
          onChange({
            processorStatus: processorStatus.length
              ? processorStatus
              : undefined,
            page: undefined,
          })
        }
      />
      {hasFilters && (
        <Button size="sm" variant="ghost" onClick={clear} className="gap-1.5">
          <XIcon className="h-3.5 w-3.5" />
          <T context="button to clear payments list filters">Clear filters</T>
        </Button>
      )}
    </>
  );
}
